import { useParams } from 'react-router-dom';
import { useApi, useAuth } from '../../shared/context';
import React, { FormEvent } from 'react';
import { StatusHistoryDrawer } from './StatusHistoryDrawer';
import {
	CerebroBreadcrumb,
	CerebroCheckbox,
	CerebroDatePicker,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroForm,
	CerebroLabel,
	CerebroLink,
	CerebroMaskedInput,
	CerebroMultiDropdown,
	CerebroMultiDropdownMenuItem,
	CerebroPageContainer,
	CerebroPrimaryButton,
	CerebroSecondaryButton,
	CerebroText,
	CerebroTextarea,
} from '../../shared/components';
import {
	Deal,
	DealClosedDiscardedReason,
	DealClosedDiscardedReasonMap,
	DealFundingLender,
	DealStatus,
	DealStatusView,
	SuccessFeeTier,
	SuccessFeeTypeMap,
} from '../../shared/models';
import { EnumUtils, FormikUtils, NumberUtils } from '../../shared/utils';
import { Field, Formik, FormikErrors } from 'formik';
import { QueryKeys, useDealStatusViewQuery, useFinderFeeAgreementsQuery } from '../../shared/hooks';
import { Alert, Grid, Skeleton } from '@mui/material';
import { useMutation, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { EmptyGuid } from '../../shared/constants';
import { DealInvoiceDrawer } from './DealInvoiceDrawer';
import { DealSubscriptions } from './DealSubscriptions';
import { DataRoomRootUrl } from '../../shared/utils/platform';
import { OnChangeTarget } from '../../shared/utils/formik';
import { validateTierStructure } from '../../shared/utils/tiers';
import { SendContractDocumentDrawer } from './SendContractDocumentDrawer';
import { ViewContractSignStatusDrawer } from './ViewContractSignStatusDrawer';
import { SubscriptionContractActionButton } from './SubscriptionContractActionButton';
import { UploadModifiedContractDrawer } from './UploadModifiedContractDrawer';
import { ContractDocumentsSendHistoryDraweProps } from './ContractDocumentsSendHistoryDrawer';
import { ESignDocumentReminderFrequencyMap } from '../../shared/models/ESignDocument';
import { ChangeReminderFrequencyDrawer } from './ChangeReminderFrequencyDrawer';
import { EditClosedFundedDealDrawer } from './EditClosedFundedDealDrawer';
import { getApiResponseData } from '../../shared/utils/axios';
import { FundingLenderDrawer } from './FundingLenderDrawer';
import { FundingLenderTable } from './FundingLenderTable';
import { DealSuccessStoryDrawer } from './DealSuccessStoryDrawer';
import { validateDateWithinPlusMinus100Years } from '../../shared/utils/date';

export const UpdateDealStatus: React.FC = () => {
	const api = useApi();
	const { currentUser } = useAuth();
	const snackbar = useSnackbar();
	const client = useQueryClient();
	const { guid } = useParams<'guid'>();
	const [statusHistory, setStatusHistory] = React.useState<boolean>(false);
	const [dealInvoices, setDealInvoices] = React.useState<boolean>(false);
	const [sendDocumentForSigning, setSendDocumentForSigning] = React.useState<boolean>(false);
	const [viewContractSignStatus, setViewContractSignStatus] = React.useState<boolean>(false);
	const [uploadModifiedContract, setUploadModifiedContract] = React.useState<boolean>(false);
	const [isProductSubscription, setIsProductSubscription] = React.useState<boolean>(false);
	const [viewPriorAgreements, setViewPriorAgreements] = React.useState<boolean>(false);
	const [changeReminderFrequency, setChangeReminderFrequency] = React.useState<boolean>(false);
	const [editClosedDeal, setEditClosedDeal] = React.useState<boolean>(false);
	const [editSuccessStory, setEditSuccessStory] = React.useState<boolean>(false);
	const [editFundingLender, setEditFundingLender] = React.useState<DealFundingLender | null>(null);

	const dealStatusQuery = useDealStatusViewQuery(guid);
	const [bankContactGuid, setBankContactGuid] = React.useState<string | null>(null);
	const agreementQuery = useFinderFeeAgreementsQuery(bankContactGuid, {
		enabled: bankContactGuid != null && bankContactGuid != EmptyGuid && dealStatusQuery.isSuccess,
		retry: false,
	});

	let tiers: SuccessFeeTier[] = [];
	let validateTiers = false;

	let paymentTermsComplete = true;
	if (dealStatusQuery.data && dealStatusQuery.data.companySubscription) {
		const companySubscription = dealStatusQuery.data.companySubscription;
		if (companySubscription.successFeeType == SuccessFeeTypeMap.Tiered) {
			validateTiers = true;
			tiers = companySubscription.successFeeTiers;
		}
		if (companySubscription.subscriptionFeePaymentSchedule == 'Custom') {
			paymentTermsComplete = companySubscription.paymentTermsComplete;
		}
	} else if (dealStatusQuery.data && dealStatusQuery.data.subscription) {
		const productSubscription = dealStatusQuery.data.subscription;
		if (productSubscription.successFeeType == SuccessFeeTypeMap.Tiered) {
			validateTiers = true;
			tiers = productSubscription.successFeeTiers as SuccessFeeTier[];
		}
		if (productSubscription && productSubscription.platformAccessPaymentSchedule == 'Custom') {
			paymentTermsComplete = productSubscription.paymentTermsComplete;
		}
	}

	const dealStatusList = (deal: Deal): DealStatus[] => {
		let list = [] as DealStatus[];

		if (deal.dealStatus == 'Accepted') {
			list.push('Accepted');
			list.push('MoreInfoNeeded');
			list.push('Declined');
			list.push('ContractSigned');
			list.push('Inactive');
		} else if (deal.dealStatus == 'ReadyForReview') {
			list.push('ReadyForReview');
			list.push('Accepted');
			list.push('MoreInfoNeeded');
			list.push('Declined');
		} else if (deal.dealStatus == 'Started') {
			list.push('Started');
			list.push('ReadyForReview');
		} else if (deal.dealStatus == 'ContractSigned') {
			list.push('ContractSigned');
			list.push('ClosedDiscarded');
			list.push('Paused');
		} else if (
			deal.dealStatus == 'Active' ||
			deal.dealStatus == 'TermSheetIssued' ||
			deal.dealStatus == 'ActiveClosing' ||
			deal.dealStatus == 'ClosedFunded' ||
			deal.dealStatus == 'ClosedDiscarded' ||
			deal.dealStatus == 'Paused'
		) {
			list.push('Active');
			list.push('TermSheetIssued');
			list.push('ActiveClosing');
			list.push('ClosedFunded');
			list.push('ClosedDiscarded');
			list.push('Paused');
		} else {
			list.push('Started');
			list.push('ReadyForReview');
			list.push('MoreInfoNeeded');
			list.push('Accepted');
			list.push('Declined');
			list.push('ContractSigned');

			if (deal.dealStatus == 'Inactive') {
				list.push('Inactive');
			}
		}

		return list;
	};

	const onDrawersClose = () => {
		setStatusHistory(false);
		setDealInvoices(false);
		setSendDocumentForSigning(false);
		setViewContractSignStatus(false);
		setUploadModifiedContract(false);
		setViewPriorAgreements(false);
		setChangeReminderFrequency(false);
	};

	const statusUpdateMutation = useMutation(
		async ({ values }: UpdateDealStatusMutationVariables) => {
			const patchValues = {
				dealStatus: values.dealStatus,
				closedDiscardedId: values.closedDiscardedId,
				closedDiscardedNotes: values.closedDiscardedNotes,
				pausedReasonId: values.pausedReasonId,
				pausedReasonNotes: values.pausedReasonNotes,
				followUpDate: values.followUpDate,
				fundingLenderId: values.fundingLenderId,
				finderFeeAgreementGuid: values.finderFeeAgreementGuid,
				optOutLenders: values.optOutLenders,
				notifyOptedOutLenders: values.notifyOptedOutLenders,
				selectedTermSheets: values.selectedTermSheets,
				amountFunded: NumberUtils.ParseDecimal(values.amountFunded),
				initialAmountFunded: NumberUtils.ParseDecimal(values.initialAmountFunded),
				dateFunded: values.dateFunded,
				isCompanySubscriptionRefinance: values.isCompanySubscriptionRefinance,
				companySubscriptionRefinanceIncrementalAmount: NumberUtils.ParseDecimal(
					values.companySubscriptionRefinanceIncrementalAmount
				),
				reasonForUsingCerebro: values.reasonForUsingCerebro,
				whySelectedTermSheet: values.whySelectedTermSheet,
				topDealChallenge1: values.topDealChallenge1,
				topDealChallenge2: values.topDealChallenge2,
				topDealChallenge3: values.topDealChallenge3,
			};

			const { data, error } = await getApiResponseData<DealStatusView>(
				api.patch(CerebroApiEndpoints.Deals.DealStatus(guid), patchValues)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: (data, { helpers, values }) => {
				client.invalidateQueries(QueryKeys.Deal.UpdateStatus(guid));
				client.invalidateQueries(QueryKeys.Deal.Root(guid));
				client.invalidateQueries(QueryKeys.StatusHistory(guid));
				snackbar.enqueueSnackbar('Deal status successfully updated.', { variant: 'success' });
				helpers.resetForm({
					values: {
						dealStatus: values.dealStatus,
						optOutLenders: false,
						notifyOptedOutLenders: false,
						closedDiscardedId: values.closedDiscardedId,
						closedDiscardedNotes: values.closedDiscardedNotes,
						pausedReasonId: values.pausedReasonId,
						pausedReasonNotes: values.pausedReasonNotes,
						followUpDate: values.followUpDate,
						amountFunded: values.amountFunded,
						initialAmountFunded: values.initialAmountFunded,
						dateFunded: values.dateFunded,
						fundingLenderId: values.fundingLenderId,
						selectedTermSheets:
							data?.activeTermSheets?.filter(s => s.responseStatus == 'Accepted').map(s => s.guid) ??
							([] as string[]),
						isCompanySubscriptionRefinance: values.isCompanySubscriptionRefinance,
						companySubscriptionRefinanceIncrementalAmount:
							values.companySubscriptionRefinanceIncrementalAmount,
						finderFeeAgreementGuid: values.finderFeeAgreementGuid,
						reasonForUsingCerebro: values.reasonForUsingCerebro,
						whySelectedTermSheet: values.whySelectedTermSheet,
						topDealChallenge1: values.topDealChallenge1,
						topDealChallenge2: values.topDealChallenge2,
						topDealChallenge3: values.topDealChallenge3,
					},
				});
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
		}
	);

	var validateForm = (values: UpdateDealStatusFormValues): UpdateDealStatusFormErrors => {
		const errors: UpdateDealStatusFormErrors = {};

		if (values.dealStatus == 'ActiveClosing') {
			if (!values.selectedTermSheets || values.selectedTermSheets.length == 0) {
				errors.selectedTermSheets = 'Must select at least 1 active term sheet.';
			}
		} else if (values.dealStatus == 'ClosedFunded') {
			const fundingLenderCount = dealStatusQuery.data?.fundingLenders.length ?? 0;

			if (fundingLenderCount == 0) {
				if (!values.fundingLenderId || values.fundingLenderId == EmptyGuid) {
					errors.fundingLenderId = 'You must select the lender.';
				}
				const amountFunded = NumberUtils.ParseDecimal(values.amountFunded);
				if (!amountFunded || amountFunded <= 0) {
					errors.amountFunded = 'Valid funded amount is required.';
				}
				const initialAmountFunded = NumberUtils.ParseDecimal(values.initialAmountFunded);
				if (!initialAmountFunded || initialAmountFunded <= 0) {
					errors.initialAmountFunded = 'Valid funded amount is required.';
				}
			}

			if (!values.dateFunded) {
				errors.dateFunded = 'Must select a valid date funded.';
			} else {
				const dateError = validateDateWithinPlusMinus100Years(values.dateFunded, 'Date Funded');
				if (dateError) errors.dateFunded = dateError;
			}

			if (dealStatusQuery.data?.companySubscription && values.isCompanySubscriptionRefinance) {
				const benefitType = dealStatusQuery.data.companySubscription.refinanceBenefitType;
				if (benefitType === 'IncrementAmountOnly') {
					if (
						values.companySubscriptionRefinanceIncrementalAmount == null ||
						values.companySubscriptionRefinanceIncrementalAmount === ''
					) {
						errors.companySubscriptionRefinanceIncrementalAmount =
							'Must at least enter a $0 amount if there is no incremental funds.';
					}
				}
			}
		} else if (values.dealStatus == 'ClosedDiscarded') {
			if (!values.closedDiscardedId) {
				errors.closedDiscardedId = 'Must select a discard reason.';
			}
			if (!values.closedDiscardedNotes) {
				errors.closedDiscardedNotes = 'Must enter notes regarding the deal being discarded.';
			}
		} else if (values.dealStatus == 'Paused') {
			if (!values.pausedReasonId) {
				errors.pausedReasonId = 'Must select a paused reason.';
			}
			if (!values.pausedReasonNotes) {
				errors.pausedReasonNotes = 'Must enter notes regarding the deal being paused.';
			}
			if (!values.followUpDate) {
				errors.followUpDate = 'Must enter a follow up date.';
			} else {
				const dateError = validateDateWithinPlusMinus100Years(values.followUpDate, 'Follow Up Date');
				if (dateError) errors.followUpDate = dateError;
			}
		} else if (values.dealStatus == 'ContractSigned') {
			if (dealStatusQuery.data && dealStatusQuery.data.companySubscription) {
				const companySubscription = dealStatusQuery.data.companySubscription;
				if (companySubscription.subscriptionFeePaymentSchedule == 'Custom') {
					const termsAmount = companySubscription.paymentTerms?.reduce((n, { amount }) => n + amount, 0) ?? 0;
					const subscriptionAmount = companySubscription?.subscriptionFee ?? 0;
					if (termsAmount != subscriptionAmount) {
						errors.dealStatus = `For custom payment terms, you must create terms that equal the subscription fee. Click the Action -> Edit Payment Terms to make changes.`;
					}
				}
				if (
					!companySubscription.agreementSignedBy ||
					!companySubscription.agreementSignedDate ||
					!companySubscription.agreementFileName
				) {
					errors.dealStatus = `Must have a signed subscription in order to go to ${EnumUtils.GetDealStatusDescription(
						values.dealStatus
					)} deal status.`;
				}
			} else {
				if (!dealStatusQuery.data?.subscription) {
					errors.dealStatus = `You must have a subscription in order to go to ${EnumUtils.GetDealStatusDescription(
						values.dealStatus
					)} deal status.`;
				}
				const productSubscription = dealStatusQuery.data?.subscription;

				if (productSubscription && productSubscription.platformAccessPaymentSchedule == 'Custom') {
					const termsAmount = productSubscription.paymentTerms?.reduce((n, { amount }) => n + amount, 0) ?? 0;
					const subscriptionAmount = productSubscription.setupFee ?? 0;
					if (termsAmount != subscriptionAmount) {
						errors.dealStatus = `For custom payment terms, you must create terms that equal the subscription fee. Click the Action -> Edit Payment Terms to make changes.`;
					}
				}
				if (
					productSubscription &&
					(productSubscription.setupFee > 0 ||
						(productSubscription.successFeeType == 'Tiered' &&
							productSubscription.successFeeTiers.length > 0))
				) {
					if (
						!productSubscription.agreementSignedBy ||
						!productSubscription.agreementSignedDate ||
						!productSubscription.agreementFileName
					) {
						errors.dealStatus = `Must have a signed subscription in order to go to ${EnumUtils.GetDealStatusDescription(
							values.dealStatus
						)} deal status.`;
					}
				}
			}
		}

		return errors;
	};

	const isClosedFunded = dealStatusQuery.data?.deal.dealStatus == 'ClosedFunded';
	const canEditClosedDeals =
		currentUser.isEmployeeUser &&
		currentUser.controlledObjects.filter(w => w.type == 'EditClosedFundedDeals').length > 0;

	return (
		<CerebroPageContainer maxWidth={'90%'}>
			<CerebroBreadcrumb>Update Deal Status</CerebroBreadcrumb>

			<Grid container sx={{ marginBottom: 3 }}>
				<Grid item xs={12}>
					<CerebroSecondaryButton sx={{ marginRight: 1 }} onClick={() => setDealInvoices(true)}>
						Invoices
					</CerebroSecondaryButton>
					<CerebroSecondaryButton sx={{ marginRight: 1 }} onClick={() => setStatusHistory(true)}>
						Status History
					</CerebroSecondaryButton>
					{isClosedFunded && (
						<CerebroPrimaryButton onClick={() => setEditSuccessStory(true)} sx={{ marginRight: 1 }}>
							Deal Success Story
						</CerebroPrimaryButton>
					)}
				</Grid>
			</Grid>

			{dealStatusQuery.isLoading ? (
				<>
					<Skeleton height={60} />
					<Skeleton height={60} />
				</>
			) : (
				<DealSubscriptions dealStatus={dealStatusQuery.data} isClosedFunded={isClosedFunded} />
			)}

			{!!tiers && validateTiers && !validateTierStructure(tiers) && (
				<Alert severity="info">Success fee tiers are invalid, please correct to view contract document.</Alert>
			)}
			{!paymentTermsComplete && (
				<Alert severity="info">Payment terms are invalid, please correct to view contract document.</Alert>
			)}

			{!!dealStatusQuery.data?.eSignDocument && (
				<>
					<CerebroText sx={{ marginBottom: 1 }}>
						Contract sent for signature by {dealStatusQuery.data?.eSignDocument.senderName} on{' '}
						<span style={{ marginTop: '11px' }}>
							{dealStatusQuery.data?.eSignDocument?.lastSentLocalTime}
						</span>
					</CerebroText>

					{dealStatusQuery.data?.eSignDocument.reminderFrequency != 'None' && (
						<CerebroText sx={{ marginBottom: 2 }}>
							Reminder Frequency:{' '}
							{ESignDocumentReminderFrequencyMap[dealStatusQuery.data?.eSignDocument.reminderFrequency]}
						</CerebroText>
					)}
				</>
			)}

			{showCompanySubscriptionContractDetails(dealStatusQuery.data) &&
				paymentTermsComplete &&
				showActionButton(dealStatusQuery.data) && (
					<SubscriptionContractActionButton
						agreementFilename={dealStatusQuery.data?.companySubscription?.agreementFileName}
						view={dealStatusQuery.data}
						downloadUrl={CerebroApiEndpoints.Deals.CompanySubscriptionContractDownload(
							guid,
							dealStatusQuery.data?.companySubscription?.guid
						)}
						onSendContract={() => {
							setIsProductSubscription(false);
							setSendDocumentForSigning(true);
						}}
						onViewStatus={() => setViewContractSignStatus(true)}
						onUploadContract={() => setUploadModifiedContract(true)}
						onViewPriorAgreements={() => setViewPriorAgreements(true)}
						onChangeReminderFrequency={() => setChangeReminderFrequency(true)}
					/>
				)}

			{showProductSubscriptionContractDetails(dealStatusQuery.data) &&
				paymentTermsComplete &&
				showActionButton(dealStatusQuery.data) && (
					<SubscriptionContractActionButton
						agreementFilename={dealStatusQuery.data?.subscription?.agreementFileName}
						view={dealStatusQuery.data}
						downloadUrl={CerebroApiEndpoints.Deals.SubscriptionContractDownload(
							guid,
							dealStatusQuery.data?.subscription?.guid
						)}
						onSendContract={() => {
							setIsProductSubscription(true);
							setSendDocumentForSigning(true);
						}}
						onViewStatus={() => setViewContractSignStatus(true)}
						onUploadContract={() => setUploadModifiedContract(true)}
						onViewPriorAgreements={() => setViewPriorAgreements(true)}
						onChangeReminderFrequency={() => setChangeReminderFrequency(true)}
					/>
				)}

			{dealStatusQuery.isSuccess && (
				<Formik
					enableReinitialize={true}
					initialValues={
						{
							dealStatus: dealStatusQuery.data.deal.dealStatus,
							optOutLenders: false,
							notifyOptedOutLenders: false,
							closedDiscardedId: dealStatusQuery.data.deal.dealClosedDiscardedReason,
							closedDiscardedNotes: dealStatusQuery.data.deal.dealClosedDiscardedNotes,
							pausedReasonId: dealStatusQuery.data.deal.dealPausedReason,
							pausedReasonNotes: dealStatusQuery.data.deal.dealPausedNotes,
							followUpDate: dealStatusQuery.data.deal.followUpDate,
							amountFunded:
								dealStatusQuery.data.deal.amountFunded > 0
									? NumberUtils.FormatDecimal(dealStatusQuery.data.deal.amountFunded)
									: NumberUtils.FormatDecimal(dealStatusQuery.data.deal.totalRequestedAmount),
							initialAmountFunded:
								dealStatusQuery.data.deal.amountFunded > 0
									? NumberUtils.FormatDecimal(dealStatusQuery.data.deal.amountFunded)
									: NumberUtils.FormatDecimal(dealStatusQuery.data.deal.totalRequestedAmount),
							dateFunded: dealStatusQuery.data.deal.dateFunded,
							fundingLenderId: null,
							selectedTermSheets:
								dealStatusQuery.data.activeTermSheets
									?.filter(s => s.responseStatus == 'Accepted')
									.map(s => s.guid) ?? ([] as string[]),
							isCompanySubscriptionRefinance: dealStatusQuery.data.deal.isCompanySubscriptionRefinance,
							companySubscriptionRefinanceIncrementalAmount: NumberUtils.FormatDecimal(
								dealStatusQuery.data.deal.companySubscriptionRefinanceIncrementalAmount
							),
							finderFeeAgreementGuid: null,
							reasonForUsingCerebro: dealStatusQuery.data.deal.reasonForUsingCerebro ?? '',
							whySelectedTermSheet: dealStatusQuery.data.deal.whySelectedTermSheet ?? '',
							topDealChallenge1: dealStatusQuery.data.deal.topDealChallenge1 ?? '',
							topDealChallenge2: dealStatusQuery.data.deal.topDealChallenge2 ?? '',
							topDealChallenge3: dealStatusQuery.data.deal.topDealChallenge3 ?? '',
						} as UpdateDealStatusFormValues
					}
					validate={validateForm}
					onSubmit={FormikUtils.MutateOnSubmit(statusUpdateMutation.mutate)}
				>
					{({ values, isSubmitting, errors, touched, handleSubmit, setFieldValue }) => {
						return (
							<CerebroForm onSubmit={handleSubmit}>
								<CerebroLabel htmlFor="dealStatus" id="dealStatusLabel" sx={{ marginTop: 3 }}>
									Deal Status
								</CerebroLabel>
								<Grid container spacing={3}>
									<Grid item xs={6}>
										<Field
											id="dealStatus"
											as={CerebroDropdown}
											name="dealStatus"
											disabled={isSubmitting}
											readOnly={isClosedFunded}
											error={touched.dealStatus && errors.dealStatus}
											onChange={(e: any) => {
												const changed = e.target.value as DealStatus;
												if (changed == 'ClosedFunded') {
													setFieldValue('dateFunded', new Date());
												}
												setFieldValue('dealStatus', changed);
											}}
										>
											{Object.entries(dealStatusList(dealStatusQuery.data.deal)).map(
												([value, description]) => (
													<CerebroDropdownMenuItem key={description} value={description}>
														{EnumUtils.GetDealStatusDescription(description)}
													</CerebroDropdownMenuItem>
												)
											)}
										</Field>
										{touched.dealStatus && errors.dealStatus != null && (
											<CerebroText variant="error">{errors.dealStatus}</CerebroText>
										)}
									</Grid>
								</Grid>

								{values.dealStatus == 'ContractSigned' && (
									<>
										<CerebroLabel htmlFor="reasonForUsingCerebro" sx={{ marginTop: 3 }}>
											Reason for using Cerebro
										</CerebroLabel>
										<Grid container spacing={3}>
											<Grid item xs={6}>
												<Field
													as={CerebroTextarea}
													id="reasonForUsingCerebro"
													name="reasonForUsingCerebro"
													rows={3}
													disabled={isSubmitting}
												/>
											</Grid>
										</Grid>
									</>
								)}

								{values.dealStatus == 'Active' && (
									<>
										<CerebroLabel htmlFor="topDealChallenge1" sx={{ marginTop: 3 }}>
											Top 3 Deal Challenges{' '}
											<CerebroText variant="hint" component="span">
												(for internal Deal Success Story use, not shared externally)
											</CerebroText>
										</CerebroLabel>
										<Grid container spacing={3}>
											<Grid item xs={6}>
												<Field
													as={CerebroTextarea}
													id="topDealChallenge1"
													name="topDealChallenge1"
													rows={2}
													placeholder="Challenge 1"
													disabled={isSubmitting}
												/>
												<Field
													as={CerebroTextarea}
													id="topDealChallenge2"
													name="topDealChallenge2"
													rows={2}
													placeholder="Challenge 2"
													disabled={isSubmitting}
												/>
												<Field
													as={CerebroTextarea}
													id="topDealChallenge3"
													name="topDealChallenge3"
													rows={2}
													placeholder="Challenge 3"
													disabled={isSubmitting}
												/>
											</Grid>
										</Grid>
									</>
								)}

								{dealStatusQuery.data.deal.dealStatus == 'ContractSigned' && (
									<Grid container>
										<Grid item xs={12}>
											<CerebroText>
												Use the Launch Deal link in the{' '}
												<CerebroLink
													sx={{ cursor: 'pointer' }}
													href={`${DataRoomRootUrl}/${dealStatusQuery.data.deal.dataRoomGuid}/Teasers`}
													target="_blank"
												>
													data room
												</CerebroLink>{' '}
												to move this deal from contract signed to deal launched.
											</CerebroText>
										</Grid>
									</Grid>
								)}

								{values.dealStatus == 'ActiveClosing' && (
									<>
										<Grid container spacing={3}>
											<Grid item xs={6}>
												<CerebroLabel htmlFor="selectedTermSheets" sx={{ marginTop: 3 }}>
													Select which lender(s) are going into underwriting
												</CerebroLabel>
												<Field
													id="selectedTermSheets"
													as={CerebroMultiDropdown}
													name="selectedTermSheets"
													disabled={isSubmitting}
													readOnly={isClosedFunded}
													error={touched.selectedTermSheets && errors.selectedTermSheets}
												>
													{dealStatusQuery.data.activeTermSheets.map(sheet => (
														<CerebroMultiDropdownMenuItem
															key={sheet.guid}
															value={sheet.guid}
														>
															{sheet.lenderName} ({sheet.bankName})
														</CerebroMultiDropdownMenuItem>
													))}
												</Field>
												{touched.selectedTermSheets && errors.selectedTermSheets != null && (
													<CerebroText variant="error">
														{errors.selectedTermSheets}
													</CerebroText>
												)}
											</Grid>
										</Grid>
										<Grid container spacing={3}>
											<Grid item xs={6}>
												<CerebroLabel htmlFor="optOutLenders" sx={{ marginTop: 3 }}>
													<Field
														id="optOutLenders"
														name="optOutLenders"
														as={CerebroCheckbox}
														disabled={isSubmitting}
														readOnly={isClosedFunded}
														sx={{ padding: 0, paddingRight: 1 }}
													/>
													<CerebroText variant="hint" component="span">
														Opt-out Other Lenders
													</CerebroText>
												</CerebroLabel>
											</Grid>
										</Grid>
										<Grid container spacing={3}>
											<Grid item xs={6}>
												<CerebroLabel htmlFor="notifyOptedOutLenders" sx={{ marginTop: 3 }}>
													<Field
														id="notifyOptedOutLenders"
														name="notifyOptedOutLenders"
														as={CerebroCheckbox}
														disabled={isSubmitting}
														readOnly={isClosedFunded}
														sx={{ padding: 0, paddingRight: 1 }}
													/>
													<CerebroText variant="hint" component="span">
														Notify Opted-Out Lenders
													</CerebroText>
												</CerebroLabel>
											</Grid>
										</Grid>
										<Grid container spacing={3}>
											<Grid item xs={6}>
												<CerebroLabel htmlFor="whySelectedTermSheet" sx={{ marginTop: 3 }}>
													Why they selected the chosen term sheet
												</CerebroLabel>
												<Field
													as={CerebroTextarea}
													id="whySelectedTermSheet"
													name="whySelectedTermSheet"
													rows={3}
													disabled={isSubmitting}
												/>
											</Grid>
										</Grid>
									</>
								)}

								{values.dealStatus == 'ClosedFunded' && (
									<>
										<CerebroLabel htmlFor="dateFunded" sx={{ marginTop: 3 }}>
											Date Funded
										</CerebroLabel>
										<Grid container spacing={3}>
											<Grid item xs={6}>
												<Field
													id="dateFunded"
													component={CerebroDatePicker}
													name="dateFunded"
													disabled={isSubmitting}
													readOnly={isClosedFunded}
													error={touched.dateFunded && errors.dateFunded}
												/>
												{touched.dateFunded && errors.dateFunded != null && (
													<CerebroText variant="error">{errors.dateFunded}</CerebroText>
												)}
											</Grid>
											{isClosedFunded && canEditClosedDeals && (
												<Grid item xs={6}>
													<CerebroSecondaryButton
														onClick={() => setEditClosedDeal(true)}
														sx={{ marginTop: 1 }}
													>
														Edit
													</CerebroSecondaryButton>
												</Grid>
											)}
										</Grid>
										{dealStatusQuery.data.fundingLenders.length > 0 ? (
											<FundingLenderTable
												fundingLenders={dealStatusQuery.data.fundingLenders}
												deal={dealStatusQuery.data.deal}
												onAdd={setEditFundingLender}
												onEdit={setEditFundingLender}
											/>
										) : (
											<>
												<CerebroLabel htmlFor="amountFunded" sx={{ marginTop: 3 }}>
													Total Amount Funded
												</CerebroLabel>
												<Grid container spacing={3}>
													<Grid item xs={6}>
														<Field
															id="amountFunded"
															as={CerebroMaskedInput}
															name="amountFunded"
															disabled={isSubmitting}
															readOnly={isClosedFunded}
															error={touched.amountFunded && errors.amountFunded}
															onBlur={(event: FormEvent) => {
																const target = event.target as OnChangeTarget;
																setFieldValue('initialAmountFunded', target.value);
															}}
														/>
														{touched.amountFunded && errors.amountFunded != null && (
															<CerebroText variant="error">
																{errors.amountFunded}
															</CerebroText>
														)}
													</Grid>
												</Grid>
												<CerebroLabel htmlFor="fundingLenderId" sx={{ marginTop: 3 }}>
													Funding Lender
												</CerebroLabel>
												<Grid container spacing={3}>
													<Grid item xs={12} lg={6}>
														<Field
															id="fundingLenderId"
															as={CerebroDropdown}
															name="fundingLenderId"
															disabled={isSubmitting}
															readOnly={isClosedFunded}
															error={touched.fundingLenderId && errors.fundingLenderId}
															onChange={(event: FormEvent) => {
																const target = event.target as OnChangeTarget;
																setBankContactGuid(target.value);
																setFieldValue('fundingLenderId', target.value);
															}}
														>
															{dealStatusQuery.data.activeLenders.map(lender => (
																<CerebroDropdownMenuItem
																	key={lender.bankContactGuid}
																	value={lender.bankContactGuid}
																>
																	{lender.fullName} ({lender.bankName})
																</CerebroDropdownMenuItem>
															))}
														</Field>
														{touched.fundingLenderId && errors.fundingLenderId != null && (
															<CerebroText variant="error">
																{errors.fundingLenderId}
															</CerebroText>
														)}
													</Grid>
												</Grid>
												<CerebroLabel htmlFor="initialAmountFunded" sx={{ marginTop: 3 }}>
													Initial Lender Amount Funded
												</CerebroLabel>
												<Grid container spacing={3}>
													<Grid item xs={6}>
														<Field
															id="initialAmountFunded"
															as={CerebroMaskedInput}
															name="initialAmountFunded"
															disabled={isSubmitting}
															readOnly={isClosedFunded}
															error={
																touched.initialAmountFunded &&
																errors.initialAmountFunded
															}
														/>
														{touched.initialAmountFunded &&
															errors.initialAmountFunded != null && (
																<CerebroText variant="error">
																	{errors.initialAmountFunded}
																</CerebroText>
															)}
													</Grid>
												</Grid>
												<CerebroLabel htmlFor="finderFeeAgreementGuid" sx={{ marginTop: 3 }}>
													Finder Fee Agreement
												</CerebroLabel>
												<Grid container spacing={3}>
													<Grid item xs={12}>
														<Field
															id="finderFeeAgreementGuid"
															as={CerebroDropdown}
															name="finderFeeAgreementGuid"
															disabled={isSubmitting}
															readOnly={isClosedFunded}
															error={
																touched.finderFeeAgreementGuid &&
																errors.finderFeeAgreementGuid
															}
														>
															{agreementQuery.data?.map(agreement => (
																<CerebroDropdownMenuItem
																	key={agreement.guid}
																	value={agreement.guid}
																>
																	{agreement.scope} |{' '}
																	{EnumUtils.GetTier3ContactTypeDescription(
																		agreement.tier3ContactType
																	)}
																</CerebroDropdownMenuItem>
															))}
														</Field>
														{!isClosedFunded && (
															<CerebroText variant="hint">
																If the finder fee agreement list is empty or this deal
																does not require the lender to pay, leave this option
																blank.
															</CerebroText>
														)}
														{touched.finderFeeAgreementGuid &&
															errors.finderFeeAgreementGuid != null && (
																<CerebroText variant="error">
																	{errors.finderFeeAgreementGuid}
																</CerebroText>
															)}
													</Grid>
												</Grid>
											</>
										)}
										{dealStatusQuery.data.companySubscription && (
											<>
												{!isClosedFunded && (
													<Grid container spacing={3}>
														<Grid item xs={6}>
															<CerebroLabel
																htmlFor="isCompanySubscriptionRefinance"
																sx={{ marginTop: 3 }}
															>
																<Field
																	id="isCompanySubscriptionRefinance"
																	name="isCompanySubscriptionRefinance"
																	as={CerebroCheckbox}
																	disabled={isSubmitting}
																	readOnly={isClosedFunded}
																	sx={{ padding: 0, paddingRight: 1 }}
																/>
																<CerebroText variant="hint" component="span">
																	Is this deal a refinance of a previous deal closed
																	on this company subscription?
																</CerebroText>
															</CerebroLabel>
														</Grid>
													</Grid>
												)}

												{values.isCompanySubscriptionRefinance &&
													dealStatusQuery.data.companySubscription.refinanceBenefitType ==
														'PercentDiscount' && (
														<Grid container spacing={3}>
															<Grid item xs={6} marginTop={2}>
																<CerebroText>
																	A{' '}
																	{
																		dealStatusQuery.data.companySubscription
																			.refinanceSuccessFeeDiscount
																	}
																	% discount will be applied to the success fee for
																	this deal.
																</CerebroText>
															</Grid>
														</Grid>
													)}

												{values.isCompanySubscriptionRefinance &&
													dealStatusQuery.data.companySubscription.refinanceBenefitType ==
														'IncrementAmountOnly' && (
														<>
															<CerebroLabel
																htmlFor="companySubscriptionRefinanceIncrementalAmount"
																sx={{ marginTop: 3 }}
															>
																Incremental Refinance Amount
															</CerebroLabel>
															<Grid container spacing={3}>
																<Grid item xs={6}>
																	<Field
																		id="companySubscriptionRefinanceIncrementalAmount"
																		as={CerebroMaskedInput}
																		name="companySubscriptionRefinanceIncrementalAmount"
																		disabled={isSubmitting}
																		readOnly={isClosedFunded}
																		error={
																			touched.companySubscriptionRefinanceIncrementalAmount &&
																			errors.companySubscriptionRefinanceIncrementalAmount
																		}
																	/>
																	<CerebroText variant="hint">
																		The success fee will be calculated based on the
																		incremental amount financed, not the full amount
																		funded.
																	</CerebroText>
																	{touched.companySubscriptionRefinanceIncrementalAmount &&
																		errors.companySubscriptionRefinanceIncrementalAmount !=
																			null && (
																			<CerebroText variant="error">
																				{
																					errors.companySubscriptionRefinanceIncrementalAmount
																				}
																			</CerebroText>
																		)}
																</Grid>
															</Grid>
														</>
													)}
											</>
										)}
									</>
								)}

								{values.dealStatus == 'ClosedDiscarded' && (
									<>
										<CerebroLabel htmlFor="closedDiscardedId" sx={{ marginTop: 3 }}>
											Closed Discarded Reason
										</CerebroLabel>
										<Grid container spacing={3}>
											<Grid item xs={12}>
												<Field
													id="closedDiscardedId"
													as={CerebroDropdown}
													name="closedDiscardedId"
													disabled={isSubmitting}
													readOnly={isClosedFunded}
													error={touched.closedDiscardedId && errors.closedDiscardedId}
												>
													{Object.entries(DealClosedDiscardedReasonMap).map(
														([value, description]) => (
															<CerebroDropdownMenuItem key={value} value={value}>
																{description}
															</CerebroDropdownMenuItem>
														)
													)}
												</Field>
												{touched.closedDiscardedId && errors.closedDiscardedId != null && (
													<CerebroText variant="error">
														{errors.closedDiscardedId}
													</CerebroText>
												)}
											</Grid>
										</Grid>
										<CerebroLabel htmlFor="closedDiscardedNotes" sx={{ marginTop: 3 }}>
											Closed Discarded Notes
										</CerebroLabel>
										<Grid container spacing={3}>
											<Grid item xs={12}>
												<Field
													id="closedDiscardedNotes"
													as={CerebroTextarea}
													rows={4}
													name="closedDiscardedNotes"
													disabled={isSubmitting}
													readOnly={isClosedFunded}
													error={touched.closedDiscardedNotes && errors.closedDiscardedNotes}
												/>
												{touched.closedDiscardedNotes &&
													errors.closedDiscardedNotes != null && (
														<CerebroText variant="error">
															{errors.closedDiscardedNotes}
														</CerebroText>
													)}
											</Grid>
										</Grid>
									</>
								)}

								{values.dealStatus == 'Paused' && (
									<>
										<CerebroLabel htmlFor="pausedReasonId" sx={{ marginTop: 3 }}>
											Paused Reason
										</CerebroLabel>
										<Grid container spacing={3}>
											<Grid item xs={12}>
												<Field
													id="pausedReasonId"
													as={CerebroDropdown}
													name="pausedReasonId"
													disabled={isSubmitting}
													readOnly={isClosedFunded}
													error={touched.pausedReasonId && errors.pausedReasonId}
												>
													{Object.entries(DealClosedDiscardedReasonMap).map(
														([value, description]) => (
															<CerebroDropdownMenuItem key={value} value={value}>
																{description}
															</CerebroDropdownMenuItem>
														)
													)}
												</Field>
												{touched.pausedReasonId && errors.pausedReasonId != null && (
													<CerebroText variant="error">{errors.pausedReasonId}</CerebroText>
												)}
											</Grid>
										</Grid>
										<CerebroLabel htmlFor="pausedReasonNotes" sx={{ marginTop: 3 }}>
											Paused Reason Notes
										</CerebroLabel>
										<Grid container spacing={3}>
											<Grid item xs={12}>
												<Field
													id="pausedReasonNotes"
													as={CerebroTextarea}
													rows={4}
													name="pausedReasonNotes"
													disabled={isSubmitting}
													readOnly={isClosedFunded}
													error={touched.pausedReasonNotes && errors.pausedReasonNotes}
												/>
												{touched.pausedReasonNotes && errors.pausedReasonNotes != null && (
													<CerebroText variant="error">
														{errors.pausedReasonNotes}
													</CerebroText>
												)}
											</Grid>
										</Grid>
										<CerebroLabel htmlFor="followUpDate" sx={{ marginTop: 3 }}>
											Follow Up Date
										</CerebroLabel>
										<Grid container spacing={3}>
											<Grid item xs={6}>
												<Field
													id="followUpDate"
													component={CerebroDatePicker}
													name="followUpDate"
													disabled={isSubmitting}
													readOnly={isClosedFunded}
													error={touched.followUpDate && errors.followUpDate}
												/>
												{touched.followUpDate && errors.followUpDate != null && (
													<CerebroText variant="error">{errors.followUpDate}</CerebroText>
												)}
											</Grid>
										</Grid>
									</>
								)}

								{!isClosedFunded && (
									<CerebroPrimaryButton type="submit" loading={isSubmitting} sx={{ marginTop: 6 }}>
										Save
									</CerebroPrimaryButton>
								)}
								{dealStatusQuery.data.fundingLenders.length == 0 &&
									values.dealStatus == 'ClosedFunded' && (
										<CerebroText variant="hint" sx={{ marginTop: 2 }}>
											If multiple lenders are funding this deal, they can be added after saving -
											each with separate funding dates and amounts. Include the funded amounts
											from all lenders in the Total Amount Funded entry above.
										</CerebroText>
									)}
							</CerebroForm>
						);
					}}
				</Formik>
			)}

			<StatusHistoryDrawer open={statusHistory} onClose={onDrawersClose} />
			<DealInvoiceDrawer open={dealInvoices} onClose={onDrawersClose} />
			<SendContractDocumentDrawer
				open={sendDocumentForSigning}
				eSignDoc={dealStatusQuery.data?.eSignDocument}
				dealGuid={guid}
				companySubscriptionGuid={dealStatusQuery.data?.companySubscription?.guid}
				productSubscriptionGuid={dealStatusQuery.data?.subscription?.guid}
				companyGuid={dealStatusQuery.data?.deal?.companyGuid}
				isProductSubscription={isProductSubscription}
				onClose={onDrawersClose}
			/>

			<ViewContractSignStatusDrawer
				dealGuid={guid}
				companyGuid={dealStatusQuery.data?.deal?.companyGuid}
				companySubscriptionGuid={dealStatusQuery.data?.companySubscription?.guid}
				productSubscriptionGuid={dealStatusQuery.data?.subscription?.guid}
				eSignDoc={dealStatusQuery.data?.eSignDocument}
				showUpdateStatusButton={
					dealStatusQuery.data?.subscription
						? !dealStatusQuery.data?.subscription.agreementFileName
						: dealStatusQuery.data?.companySubscription
						? !dealStatusQuery.data?.companySubscription.agreementFileName
						: false
				}
				onClose={onDrawersClose}
				open={viewContractSignStatus}
			/>

			<UploadModifiedContractDrawer
				dealGuid={guid}
				companyGuid={dealStatusQuery.data?.deal?.companyGuid}
				onClose={onDrawersClose}
				open={uploadModifiedContract}
				isCompanySubscription={!!dealStatusQuery.data?.companySubscription?.guid}
				subscriptionGuid={
					dealStatusQuery.data?.companySubscription?.guid ?? dealStatusQuery.data?.subscription?.guid
				}
			/>

			<ContractDocumentsSendHistoryDraweProps
				docs={dealStatusQuery.data?.eSignDocuments}
				open={viewPriorAgreements}
				onClose={onDrawersClose}
			/>

			<ChangeReminderFrequencyDrawer
				dealGuid={guid}
				eSignDoc={dealStatusQuery.data?.eSignDocument}
				companySubscriptionGuid={dealStatusQuery.data?.companySubscription?.guid}
				productSubscriptionGuid={dealStatusQuery.data?.subscription?.guid}
				companyGuid={dealStatusQuery.data?.deal?.companyGuid}
				isProductSubscription={isProductSubscription}
				onClose={onDrawersClose}
				open={changeReminderFrequency}
			/>

			{dealStatusQuery.isSuccess && (
				<FundingLenderDrawer
					fundingLender={editFundingLender}
					dealStatusData={dealStatusQuery.data}
					onClose={() => setEditFundingLender(null)}
				/>
			)}

			{isClosedFunded && canEditClosedDeals && dealStatusQuery.isSuccess && (
				<EditClosedFundedDealDrawer
					show={editClosedDeal}
					data={dealStatusQuery.data}
					onClose={() => setEditClosedDeal(false)}
				/>
			)}

			{isClosedFunded && dealStatusQuery.isSuccess && (
				<DealSuccessStoryDrawer
					show={editSuccessStory}
					data={dealStatusQuery.data}
					onClose={() => setEditSuccessStory(false)}
				/>
			)}
		</CerebroPageContainer>
	);
};

type UpdateDealStatusFormErrors = FormikErrors<UpdateDealStatusFormValues>;
type UpdateDealStatusMutationVariables = FormikUtils.MutateOnSubmitVariables<UpdateDealStatusFormValues>;
interface UpdateDealStatusFormValues {
	dealStatus: DealStatus;
	closedDiscardedId: DealClosedDiscardedReason | null;
	closedDiscardedNotes?: string;
	pausedReasonId?: DealClosedDiscardedReason | null;
	pausedReasonNotes?: string;
	followUpDate: Date | null;
	amountFunded?: string;
	initialAmountFunded?: string;
	dateFunded: Date | null;
	fundingLenderId: string | null;
	finderFeeAgreementGuid: string | null;
	optOutLenders: boolean;
	notifyOptedOutLenders: boolean;
	selectedTermSheets: string[];
	isCompanySubscriptionRefinance: boolean;
	companySubscriptionRefinanceIncrementalAmount?: string;
	reasonForUsingCerebro: string;
	whySelectedTermSheet: string;
	topDealChallenge1: string;
	topDealChallenge2: string;
	topDealChallenge3: string;
}

function showCompanySubscriptionContractDetails(data?: DealStatusView) {
	const subscription = data?.companySubscription;
	if (!subscription) return false;

	const hasContract = !!subscription.contractFileName;

	return subscription.successFeeType === SuccessFeeTypeMap.Tiered
		? hasContract && validateTierStructure(subscription.successFeeTiers as SuccessFeeTier[])
		: hasContract;
}

function showProductSubscriptionContractDetails(data?: DealStatusView) {
	const subscription = data?.subscription;
	if (!subscription) return false;

	const hasContract = !!subscription.contractFileName;

	return subscription.successFeeType === SuccessFeeTypeMap.Tiered
		? hasContract && validateTierStructure(subscription.successFeeTiers as SuccessFeeTier[])
		: hasContract;
}

function showActionButton(data?: DealStatusView) {
	if (data?.companySubscription)
		return (
			!data?.companySubscription?.agreementFileName ||
			(data?.companySubscription?.agreementFileName && data?.eSignDocument)
		);

	return !data?.subscription?.agreementFileName || (data?.subscription?.agreementFileName && data?.eSignDocument);
}
